import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Tone = "ok" | "info" | "warn" | "danger" | "muted" | "brand";

const TONE_CLASS: Record<Tone, string> = {
  ok: "bg-ok/15 text-ok border-ok/30",
  info: "bg-info/15 text-info border-info/30",
  warn: "bg-warn/20 text-warn border-warn/35",
  danger: "bg-danger/15 text-danger border-danger/30",
  muted: "bg-surface-2 text-fg-muted border-border",
  brand: "bg-brand/15 text-brand border-brand/30",
};

export function Badge({
  children,
  tone = "muted",
  className,
  title,
  "data-testid": testId,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  title?: string;
  /** Forwarded so tests can target a specific badge; silently dropping it hid a real assertion. */
  "data-testid"?: string;
}) {
  return (
    <span
      title={title}
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap",
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Button({
  children,
  onClick,
  variant = "default",
  size = "md",
  disabled,
  type = "button",
  className,
  title,
  "data-testid": testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md";
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
  title?: string;
  "data-testid"?: string;
}) {
  const variants = {
    default: "bg-surface hover:bg-surface-2 border-border text-fg",
    primary: "bg-brand hover:opacity-90 border-brand text-brand-fg",
    ghost: "bg-transparent hover:bg-surface-2 border-transparent text-fg-muted hover:text-fg",
    danger: "bg-danger/10 hover:bg-danger/20 border-danger/30 text-danger",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded border font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "h-6 px-2 text-xs" : "h-8 px-3 text-sm",
        // v2.10 P4.2: a finger needs 44 × 44 px, a mouse does not.
        "pointer-coarse:h-auto pointer-coarse:min-h-11 pointer-coarse:min-w-11",
        variants[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Card({
  title,
  action,
  children,
  className,
  "data-testid": testId,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "rounded-[var(--radius-card)] border bg-surface",
        className,
      )}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <h2 className="text-xs font-semibold tracking-wide uppercase text-fg-muted">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-3">{children}</div>
    </section>
  );
}

export function Field({
  label,
  children,
  hint,
  required,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-fg-muted">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-fg-muted">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded border bg-bg px-2 py-1.5 text-sm text-fg placeholder:text-fg-muted " +
  "focus:border-brand focus:outline-none pointer-coarse:min-h-11";

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="max-w-sm text-xs text-fg-muted">{hint}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 py-6 text-sm text-fg-muted">
      <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
      {label ?? "Loading…"}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p role="alert" className="rounded border border-danger/30 bg-danger/10 px-2 py-1.5 text-xs text-danger">
      {message}
    </p>
  );
}
